'use client'

/**
 * LiveEventsFeed — ticker degli eventi pubblicati dai campi in tempo reale.
 *
 * Al mount carica la history recente da /api/live/events (DB).
 * Poi ascolta i messaggi WebSocket e prepende nuovi aggiornamenti.
 */

import { useEffect, useRef, useState } from 'react'
import { MatchEvent, MatchState, TeamId, TEAMS, WsMessage, defaultMatchState } from '@/types/match'
import { AllMatchStates } from '@/hooks/useLiveMatch'
import type { EventEntry } from '@/app/api/live/events/route'

const ALL_TEAM_IDS = Object.keys(TEAMS) as TeamId[]
const MAX_ENTRIES = 40

function makeDefaults(): AllMatchStates {
  return Object.fromEntries(
    ALL_TEAM_IDS.map((id) => [id, { ...defaultMatchState, matchId: id }])
  ) as AllMatchStates
}

// Deriva una stringa leggibile da una transizione di stato
function describeTransition(
  prev: MatchState,
  next: MatchState,
  teamId: TeamId,
): string | null {
  const tc = TEAMS[teamId]
  const home = next.homeTeam || 'Casa'
  const away = next.awayTeam || 'Ospite'

  if (prev.status !== next.status) {
    switch (next.status) {
      case 'live':    return `Iniziato — ${tc.short}`
      case 'warmup':  return `Riscaldamento — ${tc.short}`
      case 'pause':   return `Pausa — ${tc.short}`
      case 'finished': {
        const w = next.winner === 'home' ? home : away
        return `Conclusa ${next.setsWon.home}–${next.setsWon.away} · ${w} vince — ${tc.short}`
      }
      case 'timeout_home': return `Timeout ${home} — ${tc.short}`
      case 'timeout_away': return `Timeout ${away} — ${tc.short}`
    }
  }

  if (next.setHistory.length > prev.setHistory.length) {
    const last = next.setHistory[next.setHistory.length - 1]
    if (last) {
      const sw = last.home > last.away ? home : away
      return `Set ${prev.currentSet} vinto da ${sw} ${last.home}–${last.away} — ${tc.short}`
    }
  }

  const dh = next.currentScore.home - prev.currentScore.home
  const da = next.currentScore.away - prev.currentScore.away
  if (dh > 0) return `${home} ${next.currentScore.home}–${next.currentScore.away} (Set ${next.currentSet}) — ${tc.short}`
  if (da > 0) return `${away} ${next.currentScore.away}–${next.currentScore.home} (Set ${next.currentSet}) — ${tc.short}`

  return null
}

type LocalEntry = EventEntry & { loading?: boolean; sourceId?: string }

export default function LiveEventsFeed() {
  const [entries, setEntries] = useState<LocalEntry[]>([])
  const [loading, setLoading]  = useState(true)
  const prevStates = useRef<AllMatchStates>(makeDefaults())
  // Set di id già presenti (history) per evitare duplicati con eventi WS
  const seenIds = useRef<Set<string>>(new Set())
  // Fallback timers per entry: se LLM non risponde entro N secondi, mostra il testo fallback
  const fallbackTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  // ── 1. Carica history dal DB ────────────────────────────────────────────────
  useEffect(() => {
    fetch('/api/live/events?limit=40')
      .then((r) => r.json())
      .then((data: EventEntry[]) => {
        setEntries(data)
        for (const e of data) seenIds.current.add(e.id)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [])

  // ── 2. WebSocket — nuovi eventi in real-time ────────────────────────────────
  useEffect(() => {
    const wsUrl = process.env.NEXT_PUBLIC_WS_URL
    if (!wsUrl) { setLoading(false); return }

    fetch('/api/live/state')
      .then((r) => r.json())
      .then((data: Partial<AllMatchStates>) => {
        prevStates.current = { ...makeDefaults(), ...data } as AllMatchStates
      })
      .catch(() => {})

    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      for (const teamId of ALL_TEAM_IDS) {
        ws.send(JSON.stringify({ action: 'subscribe', matchId: teamId }))
      }
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as WsMessage | { type: 'ping'; timestamp: string }

        if (msg.type === 'ping') {
          ws.send(JSON.stringify({ action: 'ping' }))
          return
        }

        // ── Evento specifico (ace, muro, errore, ecc.) ───────────────────────
        if (msg.type === 'event' && msg.matchId) {
          if (!('payload' in msg) || !msg.payload) return
          const liveEvent = msg.payload as MatchEvent
          const id = `e-${liveEvent.id}`
          if (seenIds.current.has(id)) return
          seenIds.current.add(id)

          const teamId = msg.matchId as TeamId
          const tc = TEAMS[teamId]
          const entry: EventEntry = {
            id,
            teamId,
            text: liveEvent.aiCommentary ?? liveEvent.description,
            accent: tc?.color ?? '#FE7200',
            ts: liveEvent.occurredAt,
          }
          setEntries((e) => [entry, ...e].slice(0, MAX_ENTRIES))
          // Nessuna commentary per annotazioni azione: l'esito (chi ha guadagnato
          // il punto) è noto solo al pannello scoring via score_update.
          return
        }

        // ── Commentary WS — aggiorna l'entry più recente per quel match ───────
        if (msg.type === 'commentary' && msg.matchId) {
          if (!('payload' in msg) || !msg.payload) return
          const commentary = (msg.payload as { commentary?: string }).commentary?.trim()
          if (!commentary) return
          const teamId = msg.matchId
          const incomingSourceId = (msg as WsMessage).sourceId
          const cutoff = Date.now() - 30_000
          setEntries((prev) => {
            // Match esatto per sourceId se disponibile.
            // Controlla sia e.sourceId (score_update entries) sia e.id (event entries, prefisso e-)
            let idx = -1
            if (incomingSourceId) {
              idx = prev.findIndex(
                (e) => e.sourceId === incomingSourceId || e.id === incomingSourceId
              )
            }
            if (idx === -1) {
              for (let i = prev.length - 1; i >= 0; i--) {
                const e = prev[i]
                if (e.teamId === teamId && e.loading && new Date(e.ts).getTime() > cutoff) {
                  idx = i
                  break
                }
              }
            }
            if (idx === -1) return prev
            return prev.map((e, i) => i === idx ? { ...e, loading: false, text: commentary } : e)
          })
          return
        }

        if (msg.type !== 'score_update' || !msg.matchId) return

        const teamId = msg.matchId as TeamId
        if (!TEAMS[teamId]) return

        if (!('payload' in msg) || !msg.payload) return
        const next = msg.payload as MatchState
        const prev = prevStates.current[teamId]
        const text = describeTransition(prev, next, teamId)

        const resetDetected =
          next.currentScore.home === 0 &&
          next.currentScore.away === 0 &&
          next.setsWon.home === 0 &&
          next.setsWon.away === 0 &&
          next.setHistory.length === 0 &&
          next.currentSet === 1 &&
          next.status === 'scheduled' &&
          (
            prev.currentScore.home !== 0 ||
            prev.currentScore.away !== 0 ||
            prev.setsWon.home !== 0 ||
            prev.setsWon.away !== 0 ||
            prev.setHistory.length > 0 ||
            prev.currentSet !== 1
          )

        prevStates.current = { ...prevStates.current, [teamId]: next }

        if (resetDetected) {
          setEntries((entries) => entries.filter((entry) => entry.teamId !== teamId))
          return
        }

        if (text) {
          const id = `ws-${Date.now()}-${teamId}`
          if (seenIds.current.has(id)) return
          seenIds.current.add(id)

          const tc = TEAMS[teamId]
          // Non mostrare il testo segnale finché la LLM non risponde (o timeout)
          const entry: LocalEntry = {
            id,
            teamId,
            text: '',
            accent: tc.color,
            ts: new Date().toISOString(),
            loading: true,
            sourceId: (msg as WsMessage).sourceId,
          }

          setEntries((e) => [entry, ...e].slice(0, MAX_ENTRIES))

          const scoreChanged =
            next.currentScore.home !== prev.currentScore.home ||
            next.currentScore.away !== prev.currentScore.away
          const timeoutStarted =
            (next.status === 'timeout_home' || next.status === 'timeout_away') &&
            prev.status !== next.status

          if (scoreChanged || timeoutStarted) {
            // Fallback dopo 10s: mostra il testo evento se LLM non ha ancora risposto
            const capturedId = id
            const capturedText = text
            const fallbackTimer = setTimeout(() => {
              fallbackTimers.current.delete(capturedId)
              setEntries((prev) =>
                prev.map((e) => e.id === capturedId ? { ...e, loading: false, text: capturedText } : e)
              )
            }, 10_000)
            fallbackTimers.current.set(capturedId, fallbackTimer)
          } else {
            // Nessun punto/timeout: nessuna commentary attesa, mostra subito
            setEntries((prev) =>
              prev.map((e) => e.id === id ? { ...e, loading: false, text } : e)
            )
          }
        }
      } catch {}
    }

    return () => {
      ws.close()
      for (const timer of fallbackTimers.current.values()) clearTimeout(timer)
      fallbackTimers.current.clear()
    }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10">
        <span className="w-1.5 h-1.5 rounded-full animate-pulse bg-[var(--theme-text-soft)]" />
      </div>
    )
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
        <div className="w-10 h-10 rounded-full border theme-divider flex items-center justify-center">
          <span className="theme-text-soft text-lg">⏱</span>
        </div>
        <p className="theme-text-soft font-display font-bold uppercase tracking-widest text-xs">
          In attesa di aggiornamenti
        </p>
        <p className="theme-text-soft text-xs max-w-[200px]">
          Gli eventi appariranno qui non appena gli operatori iniziano a trasmettere
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-1.5 pr-1">
      {entries.map((e) => (
        <div
          key={e.id}
          className="theme-card-contrast flex items-start gap-2.5 px-3 py-2 rounded-lg border"
        >
          <span
            className="mt-px flex-shrink-0 rounded-full px-1.5 py-px font-display font-bold uppercase tracking-widest text-[8px] leading-4"
            style={{ backgroundColor: `${e.accent}28`, color: e.accent }}
          >
            {TEAMS[e.teamId as TeamId]?.short ?? e.teamId}
          </span>
          <span className="theme-text-primary font-display font-bold text-sm leading-snug flex-1">
            {e.loading ? (
              <span className="flex items-center gap-1" aria-label="Generazione commentary…">
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    className="inline-block rounded-full animate-pulse"
                    style={{
                      width: 7, height: 7,
                      backgroundColor: e.accent,
                      opacity: 0.6,
                      animationDelay: `${i * 0.2}s`,
                      animationDuration: '1.2s',
                    }}
                  />
                ))}
              </span>
            ) : e.text}
          </span>
          <span className="theme-text-soft text-[10px] tabular-nums flex-shrink-0 mt-0.5">
            {new Date(e.ts).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
          </span>
        </div>
      ))}
    </div>
  )
}
